import { useState, useRef, useEffect, useLayoutEffect } from "react";
import type { RefObject } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "@/stores/auth.store";
import { adminService } from "@/services/admin.service";
import { cn, getUserInitials } from "@/lib/utils";
import { Icon } from "@/components/ui/icon"

interface UserMenuProps {
  navbarRef?: RefObject<HTMLElement | null>;
  userEmail?: string;
  userName?: string;
  userAvatar?: string;
  className?: string;
}

export function UserMenu({
  navbarRef,
  userEmail: propEmail,
  userName: propName,
  userAvatar: propAvatar,
  className,
}: UserMenuProps) {
  // Usar auth store como fonte de verdade para avatar (evita race com profile fetch)
  const { user: storeUser, signOut } = useAuthStore();
  const userEmail = propEmail ?? storeUser?.email;
  const userName = propName ?? storeUser?.name;
  const userAvatar = propAvatar ?? storeUser?.avatar;
  const [isOpen, setIsOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [menuPosition, setMenuPosition] = useState<{ top: number; right: number } | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (userEmail) {
      adminService.checkIsAdmin().then(setIsAdmin);
    }
  }, [userEmail]);

  useLayoutEffect(() => {
    if (!isOpen) return;
    const el = navbarRef?.current ?? buttonRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setMenuPosition({ top: rect.bottom + 2, right: 16 });
  }, [isOpen, navbarRef]);

  const menuMaxHeight =
    menuPosition != null
      ? `min(85dvh, calc(100dvh - ${menuPosition.top}px - 4rem - env(safe-area-inset-bottom, 0px) - 0.75rem))`
      : "85dvh";

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        buttonRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const handleSignOut = async () => {
    await signOut();
    navigate("/");
  };

  const meuDiaItem = {
    icon: "where_to_vote",
    label: "Meu Dia",
    href: "/my-games/today",
  };

  const navigationItems = [
    {
      icon: "workspace_premium",
      label: "Assinaturas",
      href: "/my-subscriptions",
    },
    {
      icon: "credit_card",
      label: "Cartões",
      href: "/my-cards",
    },
    {
      icon: "school",
      label: "CT",
      href: "/my-ct",
    },
    {
      icon: "event",
      label: "Eventos",
      href: "/my-events",
    },
    {
      icon: "photo_library",
      label: "Galeria",
      href: "/my-galleries",
    },
    {
      icon: "sports_soccer",
      label: "Jogos",
      href: "/my-games",
    },
    {
      icon: "skillet",
      label: "Panelinhas",
      href: "/panelinha",
    },
    {
      icon: "groups",
      label: "Times",
      href: "/my-teams",
    },
    {
      icon: "emoji_events",
      label: "Torneios",
      href: "/my-tournaments",
    },
  ].sort((a, b) => a.label.localeCompare(b.label, "pt-BR"));

  const renderMenuLink = (
    item: { icon: string; label: string; href: string },
    className?: string,
  ) => (
    <a
      key={item.href}
      href={item.href}
      onClick={(e) => {
        e.preventDefault();
        setIsOpen(false);
        navigate(item.href);
      }}
      className={cn(
        "flex items-center gap-3 px-4 py-2.5 my-1 text-muted-foreground dark:text-muted-foreground hover:text-primary rounded-xl hover:bg-primary/5 dark:hover:bg-primary/10 transition-all duration-200 group",
        className)}
    >
      <div className="flex items-center justify-center w-8 h-8 rounded-full bg-muted/30 dark:bg-muted text-muted-foreground dark:text-muted-foreground group-hover:bg-white dark:group-hover:bg-background group-hover:text-primary group-hover:shadow-sm transition-all duration-200">
        <Icon name={item.icon} className="size-5" />
      </div>
      <span className="text-sm font-normal">{item.label}</span>
      <Icon name="arrow_forward_ios" className={cn("ml-auto size-4 text-muted-foreground/0 group-hover:text-muted-foreground/50 transition-all -translate-x-2 group-hover:translate-x-0")} />
    </a>
  );

  const menuItems = [
    {
      section: "Navegação",
      items: navigationItems,
    },
    {
      section: "Conta",
      items: [
        {
          icon: "calendar_month",
          label: "Calendário",
          href: "/calendar",
        },
        {
          icon: "person_outline",
          label: "Perfil",
          href: "/profile",
        },
        {
          icon: "scoreboard",
          label: "Placar",
          href: "/placar",
        },
      ],
    },
    {
      section: "Suporte",
      items: [
        {
          icon: "support_agent",
          label: "Ajuda e suporte",
          href: "/support",
        },
      ],
    },
  ];

  const menuOverlay =
    isOpen && menuPosition
      ? createPortal(
          <>
            <div
              className="fixed inset-0 z-[69] bg-black/50 lg:hidden"
              onClick={() => setIsOpen(false)}
              aria-hidden="true"
            />
            <div
              ref={menuRef}
              className={cn(
                "fixed z-[70] flex max-h-[85dvh] w-[280px] flex-col overflow-hidden rounded-xl border border-border bg-card-surface font-display antialiased shadow-2xl ring-1 ring-black/5")}
              style={{
                top: menuPosition.top,
                right: menuPosition.right,
                maxHeight: menuMaxHeight,
              }}
            >
              {/* Header */}
              <div className="shrink-0 border-b border-border bg-transparent p-5 dark:border-white/5">
                <div className="flex flex-col items-center text-center">
                  <div className="mb-3 relative">
                    <div className="bg-center bg-no-repeat bg-cover rounded-full size-16 border-4 border-white dark:border-border shadow-lg overflow-hidden">
                      {userAvatar ? (
                        <img
                          alt="User Avatar"
                          className="w-full h-full object-cover"
                          src={userAvatar}
                          loading="eager"
                        />
                      ) : (
                        <div className="w-full h-full rounded-full bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center text-white font-bold text-xl">
                          {getUserInitials(userName || userEmail)}
                        </div>
                      )}
                    </div>
                  </div>
                  <p className="text-base font-bold text-foreground dark:text-white truncate w-full">
                    {userName || "Usuário"}
                  </p>
                  <p className="text-xs text-muted-foreground dark:text-muted-foreground truncate w-full">
                    {userEmail}
                  </p>
                </div>
              </div>

              {/* Menu Items */}
              <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-2 custom-scrollbar">
                {isAdmin && (
                  <a
                    href="/admin"
                    onClick={(e) => {
                      e.preventDefault();
                      setIsOpen(false);
                      navigate("/admin");
                    }}
                    className="flex items-center gap-3 px-4 py-2.5 my-1 text-amber-600 dark:text-amber-400 hover:text-amber-700 dark:hover:text-amber-300 rounded-xl hover:bg-amber-50 dark:hover:bg-amber-900/10 transition-all duration-200 group"
                  >
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-amber-100 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400 group-hover:bg-amber-200 dark:group-hover:bg-amber-900/30 group-hover:text-amber-700 group-hover:shadow-sm transition-all duration-200">
                      <Icon name="admin_panel_settings" className="size-5" />
                    </div>
                    <span className="text-sm font-normal">Admin</span>
                    <Icon name="arrow_forward_ios" className={cn("ml-auto size-4 text-muted-foreground/0 group-hover:text-muted-foreground/50 transition-all -translate-x-2 group-hover:translate-x-0")} />
                  </a>
                )}
                {isAdmin ? <div className="my-2 h-px bg-border mx-2" /> : null}
                {renderMenuLink(meuDiaItem)}
                <div className="my-2 h-px bg-border mx-2" />
                {menuItems.map((section, sectionIndex) => (
                  <div key={section.section}>
                    {sectionIndex > 0 ? <div className="my-2 h-px bg-border mx-2" /> : null}
                    {section.items.map((item) => renderMenuLink(item))}
                  </div>
                ))}

                <button
                  onClick={handleSignOut}
                  className="flex items-center gap-3 px-4 py-2.5 w-full text-left text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/10 rounded-xl transition-all duration-200 group"
                >
                  <div className="flex items-center justify-center w-8 h-8 rounded-full bg-red-50 dark:bg-red-900/10 text-red-600 dark:text-red-400 group-hover:bg-red-100 dark:group-hover:bg-red-900/20 transition-all duration-200">
                    <Icon name="logout" className="size-5" />
                  </div>
                  <span className="text-sm font-normal">Sair da Conta</span>
                </button>
              </div>
            </div>
          </>,
          document.body,
        )
      : null;

  return (
    <div className={cn("relative", className)}>
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-3 h-10 min-h-10 min-w-10 rounded-xl px-2 hover:bg-muted/50 active:bg-muted transition-colors group outline-none shrink-0"
        aria-label="Menu do usuário"
        aria-expanded={isOpen}
      >
        <span className="hidden sm:block max-w-[10rem] truncate text-sm font-semibold text-foreground">
          {userName?.trim().split(/\s+/)[0] || userEmail || "Usuário"}
        </span>
        <div className="relative">
          <div className="bg-center bg-no-repeat bg-cover rounded-full size-10 overflow-hidden ring-2 ring-primary ring-offset-2 ring-offset-background shrink-0">
            {userAvatar ? (
              <img
                alt="User Avatar"
                className="w-full h-full object-cover"
                src={userAvatar}
                loading="eager"
                decoding="sync"
              />
            ) : (
              <div className="w-full h-full rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-sm shadow-inner">
                {getUserInitials(userName || userEmail)}
              </div>
            )}
          </div>
        </div>

        <Icon
          name="expand_more"
          className={cn("size-5 text-muted-foreground transition-transform", isOpen && "rotate-180 text-primary")}
        />
      </button>

      {menuOverlay}
    </div>
  );
}
